import type { searchconsole_v1 } from "googleapis";
import { uniqueId } from "lodash-es";
import * as React from "react";
import usePromise from "react-use-promise";
import { SERVICE } from "../app/service";
import { VisibilityContext } from "../app/visibility";
import { RecordMeta } from "../clay/meta";
import { FilterDetail, QueryTableResult } from "../clay/server/api";

async function rawRequest(request: any): Promise<any> {
    return SERVICE.send({
        request,
        id: uniqueId("p"),
    });
}

export async function fetchRecord<RecordType, JsonType, BrokenJsonType>(
    meta: RecordMeta<RecordType, JsonType, BrokenJsonType>,
    id: string
): Promise<RecordType | null> {
    console.log("LOAD", meta.name, id);
    const response = await rawRequest({
        type: "RECORD",
        tableName: meta.name,
        recordId: id,
    });
    if (response.record == null) {
        return null;
    }

    return meta.fromJSON(response.record);
}

export async function maybeFetchRecord<RecordType, JsonType, BrokenJsonType>(
    meta: RecordMeta<RecordType, JsonType, BrokenJsonType>,
    id: string | null
): Promise<RecordType | null> {
    if (id === null) {
        return null;
    } else {
        return fetchRecord(meta, id);
    }
}

export async function fetchRecords<RecordType, JsonType, BrokenJsonType>(
    meta: RecordMeta<RecordType, JsonType, BrokenJsonType>
): Promise<RecordType | null> {
    const response = await rawRequest({
        type: "RECORDS",
        tableName: meta.name,
    });

    return response.records.map(meta.fromJSON);
}

export async function storeRecord<RecordType, JsonType, BrokenJsonType>(
    meta: RecordMeta<RecordType, JsonType, BrokenJsonType>,
    form: string,
    record: RecordType
): Promise<RecordType | null> {
    const response = await rawRequest({
        type: "STORE",
        tableName: meta.name,
        form,
        record: meta.toJSON(record),
    });

    return meta.fromJSON(response.record);
}

export async function localStoreRecord<RecordType, JsonType, BrokenJsonType>(
    meta: RecordMeta<RecordType, JsonType, BrokenJsonType>,
    form: string,
    record: RecordType
) {
    SERVICE.send({
        type: "LOCAL_STORE",
        request: {
            tableName: meta.name,
            form,
            record: meta.toJSON(record),
        },
        id: uniqueId("p"),
    });
}

export async function deleteRecord<RecordType, JsonType, BrokenJsonType>(
    meta: RecordMeta<RecordType, JsonType, BrokenJsonType>,
    form: string,
    recordId: string
): Promise<undefined> {
    const response = await rawRequest({
        type: "DELETE",
        tableName: meta.name,
        form,
        recordId: recordId,
    });

    return undefined;
}

export function doQuery(options: {
    tableName: string;
    columns: string[];
    sorts?: string[];
    filters?: FilterDetail[];
    limit?: number;
}): Promise<QueryTableResult> {
    return rawRequest({
        type: "QUERY",
        ...options,
    });
}

export function useQuery(
    options: {
        tableName: string;
        columns: string[];
        sorts?: string[];
        filters?: FilterDetail[];
        limit?: number;
    },
    depends: React.DependencyList,
    active = true
) {
    return useRequest<QueryTableResult["rows"]>(async () => {
        if (active) {
            return (await doQuery(options)).rows;
        } else {
            return [];
        }
    }, [...depends, active]);
}

export function useIdQuery<RecordType, JsonType, BrokenJsonType>(
    meta: RecordMeta<RecordType, JsonType, BrokenJsonType>,
    options: {
        sorts?: string[];
        filters?: FilterDetail[];
        limit?: number;
    },
    depends: React.DependencyList
): string[] | undefined {
    const [lastChange, setLastChange] = React.useState(new Date());
    const rows = useQuery(
        {
            tableName: meta.name,
            columns: ["id"],
            ...options,
        },
        [...depends, lastChange]
    );

    React.useEffect(() => {
        function callback(message: any) {
            if (message.table === meta.name) {
                setLastChange(new Date());
            }
        }
        SERVICE.addListener("cache-change", callback);
        return () => {
            SERVICE.removeListener("cache-change", callback);
        };
    }, [meta.name]);

    return React.useMemo(() => {
        if (rows === undefined) {
            return undefined;
        } else {
            return rows.map((row) => row[0] as string);
        }
    }, [rows]);
}

export function useRecordQuery<RecordType, JsonType, BrokenJsonType>(
    meta: RecordMeta<RecordType, JsonType, BrokenJsonType>,
    options: {
        sorts?: string[];
        filters?: FilterDetail[];
        limit?: number;
    },
    depends: React.DependencyList,
    active = true
) {
    const visible = React.useContext(VisibilityContext);
    const [lastChange, setLastChange] = React.useState(new Date());
    const rows = useQuery(
        {
            tableName: meta.name,
            columns: ["."],
            ...options,
        },
        [...depends, lastChange],
        active && visible
    );

    React.useEffect(() => {
        function callback(message: any) {
            if (message.table === meta.name) {
                setLastChange(new Date());
            }
        }
        SERVICE.addListener("cache-change", callback);
        return () => {
            SERVICE.removeListener("cache-change", callback);
        };
    }, [meta.name, setLastChange]);

    return React.useMemo(() => {
        if (rows === undefined) {
            return undefined;
        } else {
            return rows.map((row) => meta.fromJSON(row[0] as JsonType));
        }
    }, [rows]);
}

export function useRequest<T>(
    fn: () => Promise<T>,
    depends: React.DependencyList
) {
    const [result, error, state] = usePromise(fn, depends as any);
    return result;
}

export function useUrlInspection(
    request: searchconsole_v1.Params$Resource$Urlinspection$Index$Inspect["requestBody"],
    depends: React.DependencyList
) {
    return useRequest(async () => {
        const x = await rawRequest({
            type: "URL_INSPECTION",
            request: {
                requestBody: request,
            },
        });
        return x;
    }, depends);
}

export function useSearchAnalytics(
    request: searchconsole_v1.Params$Resource$Searchanalytics$Query,
    depends: React.DependencyList
) {
    return useRequest(async () => {
        const x = await rawRequest({
            type: "SEARCH_ANALYTICS",
            request,
        });
        return x;
    }, depends);
}
